
import { useEffect } from "react";
import { useAdminSettings } from "@/hooks/useAdminSettings";

export const useBrandColors = () => {
  const { settings, isLoading } = useAdminSettings();

  useEffect(() => {
    if (!isLoading && settings.brand?.colors) {
      const { color1, color2, color3, color4, color5 } = settings.brand.colors;
      
      // Update CSS custom properties for brand colors
      const root = document.documentElement;
      
      // Set custom CSS variables
      root.style.setProperty('--brand-color-1', color1);
      root.style.setProperty('--brand-color-2', color2);
      root.style.setProperty('--brand-color-3', color3);
      root.style.setProperty('--brand-color-4', color4);
      root.style.setProperty('--brand-color-5', color5);
      
      // Update primary colors in Tailwind
      root.style.setProperty('--primary', `${hexToHsl(color1)}`);
      root.style.setProperty('--primary-foreground', '0 0% 98%');
      
      // Update PWA theme colors dynamically - force update for Safari
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      const tileColorMeta = document.querySelector('meta[name="msapplication-TileColor"]');
      
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', color1);
        // Force Safari to recognize the change by removing and re-adding
        const newThemeColorMeta = document.createElement('meta');
        newThemeColorMeta.name = 'theme-color';
        newThemeColorMeta.content = color1;
        themeColorMeta.remove();
        document.head.appendChild(newThemeColorMeta);
      }
      if (tileColorMeta) {
        tileColorMeta.setAttribute('content', color1);
      }
      
      // Also update Apple-specific meta tags for better Safari support
      const appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (appleStatusBarMeta) {
        appleStatusBarMeta.setAttribute('content', 'default');
      }
      
      // Update the manifest.json theme color dynamically for Safari PWA
      try {
        fetch('/manifest.json')
          .then(response => response.json())
          .then(manifest => {
            manifest.theme_color = color1;
            const manifestBlob = new Blob([JSON.stringify(manifest)], {type: 'application/json'});
            const manifestURL = URL.createObjectURL(manifestBlob);
            const manifestLink = document.querySelector('link[rel="manifest"]');
            if (manifestLink) {
              manifestLink.setAttribute('href', manifestURL);
            }
          })
          .catch(console.error);
      } catch (error) {
        console.warn('Could not update manifest theme color:', error);
      }
    }
  }, [settings, isLoading]);

  return {
    brandColors: settings.brand?.colors,
    isLoading
  };
};

// Helper function to convert hex to HSL for Tailwind CSS variables
const hexToHsl = (hex: string): string => {
  // Remove the hash if present
  hex = hex.replace('#', '');
  
  // Parse the hex values
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};
